import { TableRowBase } from 'bam-node-savant-table/table-ts.ts'

// Comparators available to a percentile filter, paired with their dropdown
// labels. This array is the single source of truth: the `Comparator` union is
// derived from it and `isComparator` narrows arbitrary strings back to it.
export const COMPARATORS = [
    { value: 'gte', label: '≥' },
    { value: 'gt', label: '>' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
] as const

export type Comparator = (typeof COMPARATORS)[number]['value']

export const ComparatorDict: Record<Comparator, string> = COMPARATORS.reduce(
    (acc, curr) => {
        acc[curr.value] = curr.label
        return acc
    },
    {} as Record<Comparator, string>
)

export const isComparator = (value: string): value is Comparator =>
    COMPARATORS.some((option) => option.value === value)

// A single active percentile filter (one per table column).
export type Filter = {
    id: string
    prop: string
    comparator: Comparator
    val: number
}

// Percentile dropdown option: the underlying column field and its display name.
export type PercentileOption = {
    value: string
    label: string
}

// Row shape returned by the percentile-rankings leaderboard query
// (`select * from baseball_savant_sliders`). Only the columns surfaced by the
// table headers are typed explicitly; the index signature inherited from
// TableRowBase covers the remaining `select *` columns.
export type PercentileRankingsRow = TableRowBase & {
    href: string
    player_name: string
    player_id: number | string
    entity_id: number | string
    year: number | string
    player_type: string
    percent_rank_xwoba: number | null
    percent_rank_xba: number | null
    percent_rank_xslg: number | null
    percent_rank_xiso: number | null
    percent_rank_xobp: number | null
    percent_rank_barrel: number | null
    percent_rank_barrel_batted_rate: number | null
    percent_rank_exit_velocity_avg: number | null
    percent_rank_exit_velocity_max: number | null
    percent_rank_hard_hit_percent: number | null
    percent_rank_k_percent: number | null
    percent_rank_bb_percent: number | null
    percent_rank_whiff_percent: number | null
    percent_rank_chase_percent: number | null
    percent_speed_order: number | null
    percent_rank_oaa: number | null
    percent_rank_arm_overall: number | null
    percent_rank_fastball_velo: number | null
    percent_rank_fastball_spin: number | null
    percent_rank_cu_spin: number | null
    percent_rank_fastball_extension: number | null
    percent_rank_swing_speed: number | null
    percent_rank_squared_up_swing: number | null
    percent_rank_swing_length: number | null
}
